import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CaseDetails,
  CaseFamilyAssociation,
  CaseReferenceDetails,
  CaseUploadFile,
  CommonIP,
  CommonIPTrifold,
  CommonIPTrifoldUploadResponse,
  CommonNote,
  DEFAULT_IP_STATE,
  DocumentSubType,
  EpsWsResponse,
  EpsWsWrapper,
  FileKeyword,
  FileNotesDTO,
  FileVersionDO,
  FileWithProgress,
  GridCriteria,
  GridRequest,
  IP,
  IP_TYPE_ENDPOINT_MAP,
  ImageResponse,
  MatterDO,
  MatterDetails,
  MatterFamilyAssociation,
  MatterTrifoldData,
  MatterUploadFile,
  PatentDO,
  PatentTrifoldData,
  RefFileResponse,
  RefUploadFile,
  ReferenceDetails,
  TRIFOLD_FIELD_MAP,
  TrademarkDO,
  TrademarkDetails,
  TrademarkFamilyAssociation,
  TrademarkTrifoldData,
  TrademarkUploadFile,
  WF,
  WorkflowDO,
  WorkflowFileDO,
  WorkflowNoteDO,
  WsAccountDetails,
  WsFile,
  FileReference,
  WsFileWithData,
} from '@model/ftf-api.model';
import { Cache, CacheOptions } from '@utils/cache';
import { adjustTrifoldFile, adjustTrifoldFiles } from '@utils/sorting-utils';
import { Observable, Subject, catchError, filter, finalize, forkJoin, map, of, switchMap, takeUntil, tap, throwError } from 'rxjs';

import { AuthService } from './auth.service';
import { LoaderService } from './loader.service';
import { LoggerService } from './logger.service';
import { SettingsService } from './settings.service';
import { VersionService } from './version.service';

export interface ApiExtraOptions {
  headers?: { [header: string]: string | string[] };
  params?: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
  cache?: boolean | { key: string; options?: CacheOptions };
  skipCache?: boolean;
  skipLoader?: boolean;
}
/**
 * Creates an empty GridCriteria object.
 *
 * @param startIndex The starting index for the results.
 * @param displayNumber The number of results to display.
 * @param sortBy The field to sort by.
 * @param sortOrder The order to sort in (asc or desc).
 * @returns An empty GridCriteria object.
 */
export function emptyCriteria<T>(startIndex = 0, displayNumber = 10, sortBy = '', sortOrder = 'asc'): GridCriteria<T> {
  return {
    startIndex,
    displayNumber,
    sortBy,
    sortOrder,
    totalRows: 0,
    results: [],
  };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private settingsService = inject(SettingsService);
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private loader = inject(LoaderService);
  private versionService = inject(VersionService);
  private loggerService = inject(LoggerService);

  private readonly $ngDestroyed = new Subject<void>();

  private cache = new Cache(500, 60000);

  private baseUrl: string;

  /**
   * Initializes a new instance of the ApiService class.
   *
   * @constructor
   */
  constructor() {
    const { settingsService } = this;

    this.baseUrl = settingsService.getParsedBaseUrl();
    this.settingsService.settingsChange$.pipe(takeUntil(this.$ngDestroyed)).subscribe(() => {
      // ensure we always have most up to date url
      this.baseUrl = settingsService.getParsedBaseUrl();
    });
    this.authService.canCallApi$.pipe(takeUntil(this.$ngDestroyed)).subscribe((canCallApi) => {
      if (!canCallApi) {
        // when auth is lost or if base url is changed (different FTF instance)
        // we gotta clear our cache
        this.cache.clear();
      }
    });
  }

  /**
   * Performs a GET request to the specified endpoint.
   * @param endpoint The endpoint to send the GET request to.
   * @param extras Additional options for the request.
   * @returns An Observable of type EpsWsResponse<T> representing the response from the API.
   */
  get<T>(endpoint: string, extras: ApiExtraOptions = { cache: true }): Observable<EpsWsResponse<T>> {
    if (!extras.skipCache && extras.cache) {
      // first we check if item was cached recently
      const key = typeof extras.cache === 'boolean' ? endpoint : extras.cache.key;
      const cached = this.cache.get<EpsWsResponse<T>>(key);
      if (cached) {
        return of(cached);
      }
    }

    const headers = { Authorization: `Bearer ${this.authService.accessToken}`, ...(extras.headers ?? {}) };

    if (!extras.skipLoader) {
      this.loader.show();
    }
    const api = `${this.baseUrl}${endpoint}`;
    return this.http.get<EpsWsWrapper<T>>(api, { headers, params: extras.params ?? {}, observe: 'response' }).pipe(
      switchMap((resp) => of(resp.body!.epsws!)),
      tap((resp) => {
        if (resp.code === 200 && extras.cache) {
          // cache the sucka
          if (typeof extras.cache === 'boolean') {
            this.cache.set(endpoint, resp);
          } else {
            this.cache.set(extras.cache.key, resp, extras.cache.options);
          }
        }
      }),
      catchError((err) => {
        const resp = { code: err.status, message: err.message, data: null, type: EpsWsResponse.TypeEnum.GENERAL } as EpsWsResponse<T>;
        return of(resp);
      }),
      finalize(() => {
        this.loader.hide();
      })
    );
  }

  /**
   * Sends a POST request to the specified endpoint.
   * @param endpoint The endpoint to send the POST request to.
   * @param body The body of the POST request.
   * @param extras Additional options for the request.
   * @returns An Observable of type EpsWsResponse<T> representing the response from the API.
   */
  post<T>(endpoint: string, body: FormData | string | null | undefined, extras: ApiExtraOptions = {}): Observable<EpsWsResponse<T>> {
    const headers = { Authorization: `Bearer ${this.authService.accessToken}`, ...(extras.headers ?? {}) };
    // Example of how you might handle extras such as custom headers

    if (!extras.skipLoader) {
      this.loader.show();
    }
    const api = `${this.baseUrl}${endpoint}`;
    return this.http.post<EpsWsWrapper<T>>(api, body, { headers, params: extras.params ?? {}, observe: 'response' }).pipe(
      switchMap((resp) => of(resp.body!.epsws!)),
      catchError((err) => {
        const resp = { code: err.status, message: err.message, data: null, type: EpsWsResponse.TypeEnum.GENERAL } as EpsWsResponse<T>;
        return of(resp);
      }),
      finalize(() => {
        this.loader.hide();
      })
    );
  }

  /**
   * Fetches data from the specified endpoint, transforms the response, and returns an Observable of the transformed data.
   *
   * @template T The type of the response data.
   * @template S The type of the transformed data.
   * @param {('GET' | 'POST')} method The HTTP method to use for the request.
   * @param {string} endpoint The endpoint to send the request to.
   * @param {(resp: T) => S} transform The transformation function to apply to the response data.
   * @param {ApiExtraOptions} [options={ cache: true }] Additional options for the API request.
   * @param {*} [body] The request body (only used for POST requests).
   * @returns {Observable<S>} An Observable of the transformed data.
   * @throws {Error} If an unsupported method is provided.
   */
  private fetchAndTransform<T, S>(
    method: 'GET' | 'POST',
    endpoint: string,
    transform: (resp: T) => S,
    options: ApiExtraOptions = { cache: true },
    body?: FormData | string | null // Only used for POST requests
  ): Observable<S> {
    // Handling based on the method
    let request$: Observable<EpsWsResponse<T>>;
    if (method === 'GET') {
      request$ = this.get<T>(endpoint, options);
    } else if (method === 'POST') {
      request$ = this.post<T>(endpoint, body, options);
    } else {
      throw new Error(`Unsupported method: ${method}`);
    }

    return request$.pipe(
      tap((resp) => {
        if (resp.code !== 200) {
          throw resp;
        }
      }),
      filter((resp): resp is EpsWsResponse<T> => !!resp && !!resp.data),
      switchMap((resp) => of(transform(resp.data!)))
    );
  }

  /**
   * Performs a GET request to the specified endpoint and transforms the response.
   * @param endpoint The endpoint to send the GET request to.
   * @param transform The transformation function to apply to the response data.
   * @param options Additional options for the request.
   * @returns An Observable of the transformed data.
   */
  private getAndTransform<T, S>(endpoint: string, transform: (resp: T) => S, options: ApiExtraOptions = { cache: true }): Observable<S> {
    return this.fetchAndTransform('GET', endpoint, transform, options);
  }
  // convenience methods for POST

  /**
   * Performs a POST request to the specified endpoint and transforms the response.
   * @param endpoint The endpoint to send the POST request to.
   * @param body The body of the POST request.
   * @param transform The transformation function to apply to the response data.
   * @param options Additional options for the request.
   * @returns An Observable of the transformed data.
   */
  private postAndTransform<T, S>(
    endpoint: string,
    body: FormData | string | null,
    transform: (resp: T) => S,
    options: ApiExtraOptions = {}
  ): Observable<S> {
    return this.fetchAndTransform('POST', endpoint, transform, options, body);
  }

  /**
   * Retrieves up to N items starting with the specified search string based on the given IP type.
   * @param search - The search string.
   * @param ipType - The IP type.
   * @param searchBy - (optional) The field to search by. Defaults to 'name'.
   * @param count - (optional) The number of results.  Defaults to 10
   * @returns An Observable that emits an array of strings.
   */
  findIPItemsStartingWith(search: string, ipType: IP.TypeEnum, searchBy: 'name' | 'serial' = 'name', count = 10): Observable<string[]> {
    switch (ipType) {
      case IP.TypeEnum.Patent:
        return this.getAndTransform<PatentDO[], string[]>(
          `api/json/cases/retrieve/${searchBy}/${encodeURIComponent(search)}/${count}`,
          (resp) => resp.map((item) => item.primaryCaseNm!)
        );
      case IP.TypeEnum.Trademark:
        return this.getAndTransform<TrademarkDO[], string[]>(
          `api/json/trademarks/retrieve/${searchBy}/${encodeURIComponent(search)}/${count}`,
          (resp) => resp.map((item) => item.companyCaseNm!)
        );
      case IP.TypeEnum.Matter:
        return this.getAndTransform<MatterDO[], string[]>(
          `api/json/matters/retrieve/${searchBy}/${encodeURIComponent(search)}/${count}`,
          (resp) => resp.map((item) => item.matterNm!)
        );
      default:
        return of([]);
    }
  }

  /**
   * Finds IP items containing the specified search term and IP types.
   *
   * @param search - The search term to filter IP items.
   * @param ipTypes - An array of IP types to filter the search results. Defaults to an empty array.
   * @param currentIndex - the beginning of the subset of results to return.
   * @param displayNumber - the number of elements to return in the result set.
   * @returns An Observable of CommonIP array containing the search results.
   */
  findIPItemsContaining(
    search: string,
    ipTypes: IP.TrifTypeEnum[] = [],
    currentIndex = 0,
    displayNumber = 10
  ): Observable<GridCriteria<CommonIP>> {
    const obs = [];
    let sortBy = '';
    const searchBase: GridRequest = { quickSearch: search, currentIndex, displayNumber, sortBy, sortOrder: 'asc' };

    for (const ipType of ipTypes) {
      switch (ipType) {
        case IP.TypeEnum.Patent:
          sortBy = 'primaryCaseNm';
          obs.push(
            this.findAllIPDetails<CaseDetails, CommonIP>(ipType, { ...searchBase, sortBy }, (r) => ({
              ...r,
              results: r.results?.map((item) => this.convertIPDetails(item, ipType)),
            }))
          );
          break;
        case IP.TypeEnum.Trademark:
          sortBy = 'companyCaseNm';
          obs.push(
            this.findAllIPDetails<CaseDetails, CommonIP>(ipType, { ...searchBase, sortBy: 'companyCaseNm' }, (r) => ({
              ...r,
              results: r.results?.map((item) => this.convertIPDetails(item, ipType)),
            }))
          );
          break;
        case IP.TypeEnum.Matter:
          sortBy = 'matterNm';
          obs.push(
            this.findAllIPDetails<CaseDetails, CommonIP>(ipType, { ...searchBase, sortBy: 'matterNm' }, (r) => ({
              ...r,
              results: r.results?.map((item) => this.convertIPDetails(item, ipType)),
            }))
          );
          break;
        default:
        // nothing
      }
    }
    const criteriaBase = emptyCriteria<CommonIP>(currentIndex, displayNumber, sortBy, searchBase.sortOrder);

    if (!obs.length) {
      return of(criteriaBase);
    }
    return forkJoin(obs).pipe(
      map((resp) => {
        const sharedCrit = resp.reduce((acc, val) => {
          // combine the results
          acc.results.push(...val.results);
          acc.totalRows += val.totalRows;
          return acc;
        }, criteriaBase);
        sharedCrit.results = sharedCrit.results.sort((a, b) => a.identifier.localeCompare(b.identifier)).slice(0, displayNumber);
        return sharedCrit;
      })
    );
  }

  /**
   * Retrieves the details of a file based on the provided fileId and ipType.
   *
   * @param fileId - The ID of the file.
   * @param ipType - The type of intellectual property (IP).
   * @returns An Observable that emits the details of the file.
   * @throws Error if the provided ipType is not supported.
   */
  findFileDetails(fileId: number, ipType: IP.TypeEnum): Observable<WsFile> {
    switch (ipType) {
      case IP.TypeEnum.Patent:
      case IP.TypeEnum.Trademark:
      case IP.TypeEnum.Matter:
        return this.getAndTransform<WsFile, WsFile>(
          `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].files}/details/${encodeURIComponent(fileId)}/`,
          adjustTrifoldFile
        );
      default:
        throw new Error(`Unsupported IPType: ${ipType}`);
    }
  }

  /**
   * Retrieves the details of a reference based on the provided ID.
   *
   * @param fileId - The ID of the reference.
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An Observable that emits the ReferenceDetails object.
   */
  findReferenceDetails(fileId: number, skipCache = false): Observable<ReferenceDetails> {
    const options = { skipCache, cache: { key: `referenceid_${fileId}` } };
    return this.getAndTransform<ReferenceDetails, ReferenceDetails>(
      `api/json/reference/details/${encodeURIComponent(fileId)}/`,
      (resp) => resp,
      options
    );
  }

  /**
   * Retrieves the details of a reference based on the provided name.
   *
   * @param refName - The title of the reference.
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An Observable that emits the ReferenceDetails object.
   */
  findReferenceDetailsByName(refName: string, skipCache = false): Observable<ReferenceDetails> {
    const options = { skipCache, cache: { key: `reference_${refName}` } };
    return this.getAndTransform<ReferenceDetails, ReferenceDetails>(
      `api/json/reference/details/?title=${encodeURIComponent(refName)}`,
      (resp) => resp,
      options
    );
  }

  /**
   * Retrieves references by multiple titles and returns a map indicating which ones exist.
   *
   * @param refNames - Array of reference titles to check.
   * @returns An Observable that emits a Map where keys are the requested titles
   *          and values are either the title (if found) or null (if not found).
   */
  findReferencesByNames(refNames: string[]): Observable<Map<string, string | null>> {
    if (!refNames.length) {
      return of(new Map<string, string | null>());
    }

    const formData = new FormData();
    const resultMap = new Map<string, string | null>();
    refNames.forEach((name) => {
      resultMap.set(name, null);
      formData.append('title', name);
    });

    const requests = [
      this.postAndTransform<FileReference[], FileReference[]>('api/json/reference/retrieve/', formData, (resp) => resp, {
        cache: false,
        skipLoader: true,
      }).pipe(
        tap((refs) => {
          refs.forEach((ref) => {
            if (ref.title) {
              resultMap.set(ref.title, ref?.title);
            }
          });
        }),
        catchError((err) => {
          this.loggerService.error('Error fetching references:', err);
          return of([]);
        })
      ),
    ];

    if (requests.length === 0) {
      return of(resultMap);
    }

    return forkJoin(requests).pipe(
      map(() => resultMap),
      catchError((err) => {
        this.loggerService.error('Error in forkJoin:', err);
        return of(resultMap);
      })
    );
  }

  /**
   * Retrieves the patent references for a given identifier.
   *
   * @param identifier - The identifier of the patent.
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An observable that emits an array of CaseReferenceDetails.
   */
  findPatentReferences(identifier: string, skipCache = false): Observable<CaseReferenceDetails[]> {
    const options = { skipCache, cache: true };
    return this.getAndTransform<CaseReferenceDetails[], CaseReferenceDetails[]>(
      `api/json/reference/case/${encodeURIComponent(identifier)}/`,
      (resp) => resp,
      options
    );
  }

  /**
   * Retrieves the patent references for a given identifier without using cache.
   * @param identifier - The identifier of the patent.
   * @returns An observable that emits an array of CaseReferenceDetails.
   */
  findPatentReferencesNoCache(identifier: string): Observable<CaseReferenceDetails[]> {
    const options = { skipCache: true, cache: false };
    return this.getAndTransform<CaseReferenceDetails[], CaseReferenceDetails[]>(
      `api/json/reference/case/${encodeURIComponent(identifier)}/`,
      (resp) => resp,
      options
    );
  }

  /**
   * Retrieves Document notes based on the provided id and ipType.
   * @param id - The id of file used to retrieve the notes.
   * @param ipType - The IP type of the file
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An Observable of CommonNote containing the retrieved notes.
   */
  findDocumentNotes(id: number, ipType: IP.TypeEnum, skipCache = false): Observable<CommonNote> {
    return this.getAndTransform<FileNotesDTO, CommonNote>(
      `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].files}/retrievenotes/`,
      (resp) => ({ notes: resp.notes, createByUser: resp.createdByUser, createDate: resp.createDate }),
      { params: { fileId: id.toString() }, skipCache, cache: true }
    );
  }

  /**
   * Retrieves the workflow notes for a given ID.
   *
   * @param id - The ID of the workflow.
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An Observable of type CommonNote representing the workflow notes.
   */
  findWorkflowNotes(id: number, skipCache = false): Observable<CommonNote> {
    const options = { skipCache, cache: true };
    return this.getAndTransform<WorkflowNoteDO, CommonNote>(
      `api/json/workflows/${id}/notes`,
      (resp) => ({ notes: resp.note, createDate: resp.createDate }),
      options
    );
  }

  /**
   * Retrieves workflow files for a given workflow ID.
   *
   * @param {number} id - The ID of the workflow to retrieve files for.
   * @param {boolean} skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns {Observable<WorkflowFileDO[]>} An observable that emits the list of workflow documents.
   */
  findWorkflowFiles(id: number, skipCache: boolean = false): Observable<WorkflowFileDO[]> {
    const options = { skipCache, cache: true };
    return this.getAndTransform<WorkflowFileDO[], WorkflowFileDO[]>(`api/json/workflows/${id}/files`, (resp) => resp, options);
  }

  /**
   * Updates the status of a file.
   * @param caseNumber - The case number associated with the file.
   * @param ipType - The IP type of the file.
   * @param fileId - The ID of the file to update.
   * @param action - The action to perform on the file (lock, unlock, checkout, undocheckout).
   * @returns An observable that emits the updated file data.
   */
  updateFileStatus(
    caseNumber: string,
    ipType: IP.TrifTypeEnum,
    fileId: number,
    action: 'lock' | 'unlock' | 'checkout' | 'undocheckout'
  ): Observable<WsFileWithData> {
    const params = { caseNumber: caseNumber, fileId: fileId.toString() };
    const endpoint = `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].files}/${encodeURIComponent(action)}/`;
    if (action === 'checkout') {
      // checkout is a special case in that it returns a blob and not the WsFile
      const headers = { Authorization: `Bearer ${this.authService.accessToken}` };
      return this.http
        .post(`${this.baseUrl}${endpoint}`, null, { params, headers, responseType: 'blob' })
        .pipe(switchMap((resp) => this.findFileDetails(fileId, ipType).pipe(map((file) => ({ ...file, data: resp })))));
    } else {
      return this.postAndTransform<WsFile, WsFile>(endpoint, null, (resp) => resp, { params });
    }
  }

  /**
   * Deletes a file.
   * @param caseNumber - The case number associated with the file.
   * @param ipType - The IP type of the file.
   * @param fileId - The ID of the file to delete.
   * @returns An observable that emits the response from the API.
   */
  deleteFile(caseNumber: string, ipType: IP.TrifTypeEnum, fileId: number): Observable<EpsWsResponse<void>> {
    const params = { caseNumber: caseNumber, fileId: fileId.toString() };
    const endpoint = `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].files}/delete/${caseNumber}/file/${fileId}/`;
    return this.getAndTransform<EpsWsResponse<void>, EpsWsResponse<void>>(endpoint, (resp) => resp, {
      params,
      skipCache: true,
      cache: false,
    });
  }

  /**
   * Updates a workflow with the specified ID using the given action.
   *
   * @param id - The ID of the workflow to update.
   * @param action - The action to perform on the workflow.
   * @returns An observable that emits the updated WorkflowDO object.
   */
  updateWorkflow(id: number, action: WF.ActionEnum): Observable<WorkflowDO> {
    const options: ApiExtraOptions = {
      headers: { 'Content-Type': 'application/json' },
      params: { action },
    };
    return this.postAndTransform<WorkflowDO, WorkflowDO>(`api/json/workflows/${encodeURIComponent(id)}/`, null, (resp) => resp, options);
  }

  /**
   * Retrieves the notes for the specified identifier and IP type.
   * @param identifier - The identifier for which to retrieve the notes.
   * @param ipType - The type of IP (Intellectual Property) to search for (e.g., Trademark, Matter, Patent).
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An Observable that emits an array of CommonNote objects.
   */
  findIPNotes(identifier: string, ipType: IP.TypeEnum, skipCache = false): Observable<CommonNote[]> {
    const options = { skipCache, cache: true };
    return this.getAndTransform<CommonNote[], CommonNote[]>(
      `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].primary}/notes/retrieve/${encodeURIComponent(identifier)}/`,
      (resp) => resp,
      options
    );
  }

  /**
   * Retrieves IP workflows based on the provided identifier and IP type.
   *
   * @param identifier - The identifier used to search for IP workflows.
   * @param ipType - The type of IP.
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An observable that emits an array of WorkflowDO objects.
   */
  findIPWorkflows(identifier: string, ipType: IP.TypeEnum, skipCache = false): Observable<WorkflowDO[]> {
    const options = { skipCache, cache: true };
    return this.getAndTransform<WorkflowDO[], WorkflowDO[]>(
      `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].primary}/workflows/${encodeURIComponent(identifier)}/`,
      (resp) => resp,
      options
    );
  }

  /**
   * Finds the IP (Intellectual Property) details and trifold for a given case number and IP type.
   *
   * @param caseNumber - The case number for which to find the IP details.
   * @param ipType - The type of IP (Intellectual Property) to search for (e.g., Trademark, Matter, Patent).
   * @param skipReference - Optional. Specifies whether to skip pulling the references for the API request. Default is true.
   * @param skipWorkflow - Optional. Specifies whether to skip pulling the workflow for the API request. Default is true.
   * @param skipCache - Optional. Specifies whether to skip pulling from the cache for the API request. Default is false.
   * @returns An Observable that emits the CommonIP object containing the IP details and trifold.
   */
  findIP(caseNumber: string, ipType: IP.TrifTypeEnum, skipReference = true, skipWorkflow = true, skipCache = false): Observable<CommonIP> {
    return forkJoin([
      this.findIPDetails(caseNumber, ipType, skipCache),
      this.findIPTrifold(caseNumber, ipType, skipCache),
      ipType === 'Patent' && !skipReference ? this.findPatentReferences(caseNumber, skipCache) : of([]),
      this.findIPNotes(caseNumber, ipType, skipCache),
      skipWorkflow ? of([]) : this.findIPWorkflows(caseNumber, ipType, skipCache),
    ]).pipe(
      switchMap(([detail, trifold, references, notes, workflow]) => {
        const rec = this.convertIPDetails(detail, ipType);
        rec.detail = { references, notes, workflow, biblio: detail };
        rec.trifold = trifold;
        rec.state = { ...DEFAULT_IP_STATE };
        return of(rec);
      })
    );
  }

  /**
   * Finds all IP details based on the given IP type, search criteria, and conversion function.
   *
   * @template A - The type of the input GridCriteria.
   * @template B - The type of the output GridCriteria.
   * @param ipType - The IP type to search for.
   * @param search - The search criteria.
   * @param convert - The conversion function to transform the response.
   * @returns An Observable of the transformed GridCriteria.
   */
  private findAllIPDetails<A, B>(
    ipType: IP.TrifTypeEnum,
    search: GridRequest,
    convert: (resp: GridCriteria<A>) => GridCriteria<B>
  ): Observable<GridCriteria<B>> {
    const params = { ...{ quickSearch: '', currentIndex: 0, displayNumber: 10 }, ...search };
    const api = `api/json/${IP_TYPE_ENDPOINT_MAP[ipType].primary}/list/`;
    const options = {
      params,
      cache: false,
      headers: { 'Content-Type': 'application/json' },
    };
    return this.getAndTransform<GridCriteria<A>, GridCriteria<B>>(api, convert, options);
  }

  /**
   * Retrieves IP details based on the provided case number and IP type.
   * @param caseNumber - The case number for which to retrieve IP details.
   * @param ipType - The type of IP (e.g., Trademark, Matter, Patent).
   * @param skipCache - Optional. Specifies whether to skip the cache. Default is false.
   * @returns An Observable that emits the IP details.
   */
  private findIPDetails(
    caseNumber: string,
    ipType: IP.TrifTypeEnum,
    skipCache = false
  ): Observable<TrademarkDetails | MatterDetails | CaseDetails> {
    const options = {
      skipCache,
      cache: { key: `${ipType}Details.${caseNumber}`, options: {} },
      headers: { 'Content-Type': 'application/json' },
    };
    switch (ipType) {
      case IP.TypeEnum.Trademark:
        return this.getAndTransform<TrademarkDetails, TrademarkDetails>(
          `api/json/trademarks/details/${encodeURIComponent(caseNumber)}/`,
          (resp) => resp,
          options
        );
      case IP.TypeEnum.Matter:
        return this.getAndTransform<MatterDetails, MatterDetails>(
          `api/json/matters/details/${encodeURIComponent(caseNumber)}/`,
          (resp) => resp,
          options
        );
      case IP.TypeEnum.Patent:
      default:
        return this.getAndTransform<CaseDetails, CaseDetails>(
          `api/json/cases/details/${encodeURIComponent(caseNumber)}/`,
          (resp) => resp,
          options
        );
    }
  }

  public findTrademarkDetails(identifier: string, skipCache = false): Observable<TrademarkDetails> {
    return this.findIPDetails(identifier, IP.TrifTypeEnum.Trademark, skipCache) as Observable<TrademarkDetails>;
  }

  /**
   * Serial match callback for finding items by serial number.
   * @param value - The serial number value to match.
   * @param serialField - The field in the item that contains the serial number.
   * @param identifierField - The field in the item that contains the identifier.
   * @returns A callback function that takes an array of items and returns the matching identifier or null.
   */
  private serialMatchCallback<T>(value: string, serialField: keyof T, identifierField: keyof T) {
    return (items: T[]): string | null => {
      const foundItem = items.find(
        (item) =>
          typeof item[serialField] === 'string' &&
          (item[serialField] as string).replace(/[^a-z0-9]/gi, '').toLowerCase() === value.replace(/[^a-z0-9]/gi, '').toLowerCase()
      );
      return foundItem ? (foundItem[identifierField] as string) : null;
    };
  }

  /**
   * Finds an IP by its serial number.
   * @param serialNo - The serial number of the IP.
   * @param ipType - The type of IP (e.g., Trademark, Matter, Patent).
   * @param skipCache - Optional. Specifies whether to skip the cache. Default is false.
   * @returns An Observable that emits the found IP or an error.
   */
  findIPBySerial(serialNo: string, ipType: IP.TrifTypeEnum, skipCache = false): Observable<CommonIP> {
    const options = {
      skipCache,
      cache: true,
      headers: { 'Content-Type': 'application/json' },
    };
    // the API does not have a direct way to search by serial number exactly. So we'll do a starts with and compare
    let obj: Observable<string | null>;
    switch (ipType) {
      case IP.TypeEnum.Patent: {
        const callback = this.serialMatchCallback<PatentDO>(serialNo, 'serialNumber', 'primaryCaseNm');
        obj = this.getAndTransform<PatentDO[], string | null>(
          `api/json/cases/retrieve/serial/${encodeURIComponent(serialNo)}/1`,
          callback,
          options
        );
        break;
      }
      case IP.TypeEnum.Trademark: {
        const callback = this.serialMatchCallback<TrademarkDO>(serialNo, 'serialNumber', 'companyCaseNm');
        obj = this.getAndTransform<TrademarkDO[], string | null>(
          `api/json/trademarks/retrieve/serial/${encodeURIComponent(serialNo)}/1`,
          callback,
          options
        );
        break;
      }
      case IP.TypeEnum.Matter: {
        const callback = this.serialMatchCallback<MatterDO>(serialNo, 'serialNumber', 'matterNm');
        obj = this.getAndTransform<MatterDO[], string | null>(
          `api/json/matters/retrieve/serial/${encodeURIComponent(serialNo)}/1`,
          callback,
          options
        );
        break;
      }
      default:
        return throwError(() => new Error('Invalid IP type provided.'));
    }
    // convert the identifier to the full IP details
    return obj.pipe(
      switchMap((identifier) => {
        if (identifier) {
          return this.findIPDetails(identifier, ipType, skipCache).pipe(map((det) => this.convertIPDetails(det, ipType)));
        }
        return throwError(() => new Error(`No matching item found for serial number: ${serialNo}`));
      })
    );
  }

  /**
   * Finds the common IP for a given case number and IP type.
   *
   * @param caseNumber - The case number to search for.
   * @param ipType - The type of IP to search for.
   * @param skipCache - Optional. Indicates whether to skip the cache. Default is false.
   * @returns An Observable that emits the common IP.
   */
  findCommonIP(caseNumber: string, ipType: IP.TrifTypeEnum, skipCache = false): Observable<CommonIP> {
    return this.findIPDetails(caseNumber, ipType, skipCache).pipe(map((det) => this.convertIPDetails(det, ipType)));
  }

  /**
   * Converts the IP details to a common format.
   * @param det - The IP details to convert.
   * @param ipType - The type of IP.
   * @returns The converted common IP.
   */
  private convertIPDetails(det: TrademarkDetails | MatterDetails | CaseDetails, ipType: IP.TrifTypeEnum): CommonIP {
    switch (ipType) {
      case IP.TypeEnum.Trademark: {
        const detail = det as TrademarkDetails;
        return {
          ipType,
          identifier: detail.companyCaseNm!,
          family: detail.caseFamily!,
          caseUrl: detail.caseUrl,
          serial: detail.serialNumber,
          title: detail.trademarkNm!,
          exportControlStatus: detail.exportControlStatus,
        };
      }
      case IP.TypeEnum.Matter: {
        const detail = det as MatterDetails;
        return {
          ipType,
          identifier: detail.matterNm!,
          family: detail.matterFamilyNm!,
          caseUrl: detail.caseUrl,
          serial: detail.serialNumber,
          title: detail.matterTitle!,
          exportControlStatus: detail.exportControlStatus,
        };
      }
      case IP.TypeEnum.Patent:
      default: {
        const detail = det as CaseDetails;
        return {
          ipType,
          identifier: detail.primaryCaseNm!,
          family: detail.caseFamilyNm!,
          caseUrl: detail.caseUrl,
          serial: detail.serialNumber,
          title: detail.title!,
          exportControlStatus: detail.exportControlStatus,
        };
      }
    }
  }

  /**
   * Retrieves the IP trifold data based on the provided case number and IP type.
   * @param caseNumber - The case number for the IP.
   * @param ipType - The type of IP (e.g., Trademark, Matter, Patent).
   * @param skipCache - Optional. Specifies whether to pull fresh IP trifold data and skip any cached. Default is false.
   * @returns An Observable that emits the IP trifold data.
   */
  findIPTrifold(caseNumber: string, ipType: IP.TypeEnum, skipCache = false): Observable<CommonIPTrifold> {
    const options = { skipCache, cache: { key: `${ipType.valueOf()}_${caseNumber}` } };
    switch (ipType) {
      case IP.TypeEnum.Trademark:
        return this.getAndTransform<TrademarkTrifoldData, CommonIPTrifold>(
          `api/json/trademarkfiles/retrieve/${encodeURIComponent(caseNumber)}/`,
          (resp) => ({
            ipType: ipType,
            identifier: caseNumber,
            L: adjustTrifoldFiles(resp.client!),
            M: adjustTrifoldFiles(resp.tmOffice!),
            R: adjustTrifoldFiles(resp.oppCounsel!),
          }),
          options
        );
      case IP.TypeEnum.Matter:
        return this.getAndTransform<MatterTrifoldData, CommonIPTrifold>(
          `api/json/matterfiles/retrieve/${encodeURIComponent(caseNumber)}/`,
          (resp) => ({
            ipType: ipType,
            identifier: caseNumber,
            L: adjustTrifoldFiles(resp.correspondence!),
            M: adjustTrifoldFiles(resp.filing!),
            R: adjustTrifoldFiles(resp.documents!),
          }),
          options
        );
      case IP.TypeEnum.Patent:
      default:
        return this.getAndTransform<PatentTrifoldData, CommonIPTrifold>(
          `api/json/casefiles/retrieve/${encodeURIComponent(caseNumber)}/`,
          (resp) => ({
            ipType: ipType,
            identifier: caseNumber,
            L: adjustTrifoldFiles(resp.correspondence!),
            M: adjustTrifoldFiles(resp.filing!),
            R: adjustTrifoldFiles(resp.documents!),
          }),
          options
        );
    }
  }

  /**
   * Retrieves the user account information.
   * @returns An Observable that emits the user account details.
   */
  findUserAccountInfo(): Observable<WsAccountDetails> {
    return this.get<WsAccountDetails>('api/json/account/details/', {
      cache: { key: 'account-details', options: { maxAge: 30 * 60000, useSessionStorage: true } },
    }).pipe(switchMap((resp) => of(resp.data!)));
  }

  /**
   * Retrieves the thumbnail image for a trademark identified by the given identifier.
   *
   * @param identifier - The identifier of the trademark.
   * @returns An Observable that emits the ImageResponse object containing the thumbnail image, or null if not found.
   */
  findTrademarkThumbnail(identifier: string): Observable<ImageResponse | null> {
    const api = `api/json/trademarks/image/${encodeURIComponent(identifier)}/?thumbnail=Y&includeNotFoundImage=N`;
    return this.get<ImageResponse>(api, { cache: true, headers: { Accept: 'application/json' } }).pipe(switchMap((resp) => of(resp.data!)));
  }

  /**
   * Retrieves case family associations for a patent case.
   *
   * @param identifier - The primary case name or external reference.
   * @returns An Observable that emits an array of CaseFamilyAssociation objects.
   */
  findCaseFamilyAssociations(identifier: string): Observable<CaseFamilyAssociation[]> {
    const api = `api/json/cases/family/${encodeURIComponent(identifier)}/`;
    return this.get<CaseFamilyAssociation[]>(api, { cache: true }).pipe(switchMap((resp) => of(resp.data!)));
  }

  /**
   * Retrieves matter family associations for a matter.
   *
   * @param identifier - The matter name or external reference.
   * @returns An Observable that emits an array of MatterFamilyAssociation objects.
   */
  findMatterFamilyAssociations(identifier: string): Observable<MatterFamilyAssociation[]> {
    const api = `api/json/matters/family/${encodeURIComponent(identifier)}/`;
    return this.get<MatterFamilyAssociation[]>(api, { cache: true }).pipe(switchMap((resp) => of(resp.data!)));
  }

  /**
   * Retrieves trademark family associations for a trademark.
   *
   * @param identifier - The company case name or external reference.
   * @returns An Observable that emits an array of TrademarkFamilyAssociation objects.
   */
  findTrademarkFamilyAssociations(identifier: string): Observable<TrademarkFamilyAssociation[]> {
    const api = `api/json/trademarks/family/${encodeURIComponent(identifier)}/`;
    return this.get<TrademarkFamilyAssociation[]>(api, { cache: true }).pipe(switchMap((resp) => of(resp.data!)));
  }

  /**
   * Returns family case identifier strings for a given identifier and IP type.
   * Routes to the correct family associations endpoint and extracts the relevant
   * identifier field based on IP type.
   *
   * @param identifier - The case, matter, or trademark identifier.
   * @param ipType - The IP type to determine the correct endpoint and identifier field.
   * @returns An Observable emitting an array of family member identifier strings.
   */
  findFamilyAssociations(identifier: string, ipType: IP.TrifTypeEnum): Observable<string[]> {
    switch (ipType) {
      case IP.TrifTypeEnum.Patent:
        return this.findCaseFamilyAssociations(identifier).pipe(map((assocs) => assocs.map((a) => a.primaryCaseNm!).filter(Boolean)));
      case IP.TrifTypeEnum.Trademark:
        return this.findTrademarkFamilyAssociations(identifier).pipe(map((assocs) => assocs.map((a) => a.companyCaseNm!).filter(Boolean)));
      case IP.TrifTypeEnum.Matter:
        return this.findMatterFamilyAssociations(identifier).pipe(map((assocs) => assocs.map((a) => a.matterNm!).filter(Boolean)));
      default:
        return of([]);
    }
  }

  /**
   * Downloads the file with the specified file ID and name.
   * @param fileId - The ID of the file to download.
   * @param fileName - The name of the file to download.
   * @param ipType - The type of IP (e.g., Trademark, Matter, Patent).
   * @returns An Observable of type Blob representing the file to download.
   */
  downloadFile(fileId: number, fileName: string, ipType: IP.TypeEnum) {
    const headers = { Authorization: `Bearer ${this.authService.accessToken}` };
    const api = `${this.baseUrl}api/json/${IP_TYPE_ENDPOINT_MAP[ipType].files}/download/${fileId}/`;
    return this.http.get(api, { headers, responseType: 'blob' });
  }

  /**
   * Adds a file note to the associated file based on the given case number, and IP type.
   * @param caseNumber - The case number associated with the file.
   * @param file - The file to be uploaded.
   * @param ipType - The type of intellectual property (IP).
   * @returns An Observable that emits the response of the upload operation.
   * @throws Error if the IP type is unsupported.
   */
  addDocNoteToFile(caseNumber: string, file: FileWithProgress, ipType: IP.TrifTypeEnum): Observable<CommonIPTrifoldUploadResponse> {
    const formData = new FormData();
    formData.append('caseNumber', caseNumber);
    formData.append('fileId', file.id);
    formData.append('fileNote', file.docNotes);

    const headers = { Authorization: `Bearer ${this.authService.accessToken}` };

    switch (ipType) {
      case IP.TypeEnum.Trademark:
        formData.append('companyCaseNm', caseNumber);
        return this.postAndTransform<TrademarkUploadFile, CommonIPTrifoldUploadResponse>(
          'api/json/trademarkfiles/addfilenote/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.companyCaseNm! }),
          { headers }
        );
      case IP.TypeEnum.Matter:
        formData.append('matterNm', caseNumber);
        return this.postAndTransform<MatterUploadFile, CommonIPTrifoldUploadResponse>(
          'api/json/matterfiles/addfilenote/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.matterNm! }),
          { headers }
        );
      case IP.TypeEnum.Patent:
        formData.append('primaryCaseNm', caseNumber);
        return this.postAndTransform<CaseUploadFile, CommonIPTrifoldUploadResponse>(
          'api/json/casefiles/addfilenote/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.primaryCaseNm! }),
          { headers }
        );
      default:
        throw new Error(`Unsupported IPType: ${ipType}`);
    }
  }

  /**
   * Adds a keyword to the associated file based on the given case number, and IP type.
   * @param caseNumber - The case number associated with the file.
   * @param file - The file to be uploaded.
   * @param ipType - The type of intellectual property (IP).
   * @returns An Observable that emits the response of the upload operation.
   * @throws Error if the IP type is unsupported.
   */
  addKeywordToFile(caseNumber: string, file: FileWithProgress, ipType: IP.TrifTypeEnum): Observable<CommonIPTrifoldUploadResponse> {
    const formData = new FormData();
    formData.append('caseNumber', caseNumber);
    formData.append('fileId', file.id);
    formData.append('keywordText', file.keywords);

    const headers = { Authorization: `Bearer ${this.authService.accessToken}` };

    switch (ipType) {
      case IP.TypeEnum.Trademark:
        return this.postAndTransform<FileKeyword, CommonIPTrifoldUploadResponse>(
          'api/json/trademarkfiles/addkeyword/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.caseNumber! }),
          { headers }
        );
      case IP.TypeEnum.Matter:
        return this.postAndTransform<FileKeyword, CommonIPTrifoldUploadResponse>(
          'api/json/matterfiles/addkeyword/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.caseNumber! }),
          { headers }
        );
      case IP.TypeEnum.Patent:
        return this.postAndTransform<FileKeyword, CommonIPTrifoldUploadResponse>(
          'api/json/casefiles/addkeyword/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.caseNumber! }),
          { headers }
        );
      default:
        throw new Error(`Unsupported IPType: ${ipType}`);
    }
  }

  /**
   * Uploads a file to the trifold endpoint based on the given case number, file, and IP type.
   * @param caseNumber - The case number associated with the file.
   * @param file - The file to be uploaded.
   * @param data - The file data to be uploaded.
   * @param ipType - The type of intellectual property (IP).`
   * @returns An Observable that emits the response of the upload operation.
   * @throws Error if the IP type is unsupported.
   */
  uploadFileToTrifold(
    caseNumber: string,
    file: FileWithProgress,
    data: Blob | null,
    ipType: IP.TrifTypeEnum
  ): Observable<CommonIPTrifoldUploadResponse> {
    if (!data) {
      throw new Error('No file data provided!');
    }

    const formData = new FormData();
    formData.append('fileData', data);
    formData.append('fileName', file.name);
    formData.append('title', file.title);
    // TODO: unsure on formatting of date
    formData.append('documentDate', formatFileDateTime(file.date));
    if (file.subType) formData.append('docSubTypeId', file.subType.toString());
    formData.append('filePriority', file.urgent ? '0' : '3');
    formData.append('fileSource', `WOA v [${this.versionService.getVersion()}]`);
    formData.append('hasAttachments', file.hasAttachments ? 'Y' : 'N');
    formData.append('exportControl', file.exportControl ? 'Y' : 'N');

    const trifold = file.trifold as IP.TrifoldEnum;
    const trifoldEndpoint = TRIFOLD_FIELD_MAP[ipType][trifold].endpoint;
    const headers = { Authorization: `Bearer ${this.authService.accessToken}` };
    const skipLoader = true;

    switch (ipType) {
      case IP.TypeEnum.Trademark:
        formData.append('companyCaseNm', caseNumber);
        return this.postAndTransform<TrademarkUploadFile, CommonIPTrifoldUploadResponse>(
          `api/json/trademarks/upload/${trifoldEndpoint}/`,
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.companyCaseNm! }),
          { headers, skipLoader }
        );
      case IP.TypeEnum.Matter:
        formData.append('matterNm', caseNumber);
        return this.postAndTransform<MatterUploadFile, CommonIPTrifoldUploadResponse>(
          `api/json/matters/upload/${trifoldEndpoint}/`,
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.matterNm! }),
          { headers, skipLoader }
        );
      case IP.TypeEnum.Patent:
        formData.append('primaryCaseNm', caseNumber);
        return this.postAndTransform<CaseUploadFile, CommonIPTrifoldUploadResponse>(
          `api/json/cases/upload/${trifoldEndpoint}/`,
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.primaryCaseNm! }),
          { headers, skipLoader }
        );
      default:
        throw new Error(`Unsupported IPType: ${ipType}`);
    }
  }

  /**
   * Uploads a new version of a file associated with a specific case, matter, or patent.
   *
   * @param id - The unique identifier of the file.
   * @param caseNumber - The case number associated with the file.
   * @param fileName - The name of the file being uploaded.
   * @param data - The file data as a `Blob`. Must not be `null`.
   * @param ipType - The type of intellectual property (IP) associated with the file.
   *                 Must be one of the `IP.TrifTypeEnum` values.
   *
   * @returns An `Observable` that emits a `CommonIPTrifoldUploadResponse` containing
   *          the response data, including the IP type and identifier.
   *
   * @throws An error if no file data is provided or if the `ipType` is unsupported.
   */
  uploadFileVersion(
    id: number,
    caseNumber: string,
    fileName: string,
    data: Blob | null,
    ipType: IP.TrifTypeEnum
  ): Observable<CommonIPTrifoldUploadResponse> {
    if (!data) {
      throw new Error('No file data provided!');
    }

    const formData = new FormData();
    formData.append('fileData', new File([data], fileName));
    formData.append('fileName', fileName);
    formData.append('fileId', id + '');

    switch (ipType) {
      case IP.TypeEnum.Trademark:
        formData.append('companyCaseNm', caseNumber);
        return this.postAndTransform<TrademarkUploadFile, CommonIPTrifoldUploadResponse>(
          'api/json/trademarks/uploadversion/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.companyCaseNm! })
        );
      case IP.TypeEnum.Matter:
        formData.append('matterNm', caseNumber);
        return this.postAndTransform<MatterUploadFile, CommonIPTrifoldUploadResponse>(
          'api/json/matters/uploadversion/',
          formData,
          (resp) => ({ ...resp, ipType: ipType, identifier: resp.matterNm! })
        );
      case IP.TypeEnum.Patent:
        formData.append('primaryCaseNm', caseNumber);
        return this.postAndTransform<CaseUploadFile, CommonIPTrifoldUploadResponse>('api/json/cases/uploadversion/', formData, (resp) => ({
          ...resp,
          ipType: ipType,
          identifier: resp.primaryCaseNm!,
        }));
      default:
        throw new Error(`Unsupported IPType: ${ipType}`);
    }
  }

  uploadFileToReference(
    caseNumber: string | null,
    file: FileWithProgress,
    data: Blob | null,
    ipType: IP.TypeEnum
  ): Observable<RefFileResponse> {
    if (!data) {
      throw new Error('No file data provided!');
    }

    const formData = new FormData();
    // if CaseRefLibrary selected just add this part and it will upload to specific case
    if (ipType === IP.TypeEnum.CaseReference) {
      if (caseNumber) {
        formData.append('assocType', 'primaryCaseNm');
        formData.append('assocValue', caseNumber);
      } else {
        throw new Error('No case number provided for CaseReference upload!');
      }
    }
    formData.append('fileData', data);
    formData.append('title', file.title);
    formData.append('fileName', file.name);
    if (file.referenceType !== undefined) {
      formData.append('referenceType', file.referenceType.toString());
    }
    formData.append('fileSource', `WOA v [${this.versionService.getVersion()}]`);

    const headers = { Authorization: `Bearer ${this.authService.accessToken}` };
    const skipLoader = true;
    return this.postAndTransform<RefUploadFile, RefFileResponse>(
      'api/json/reference/upload/',
      formData,
      (resp) => ({ ...resp, uploadUserName: resp.uploadUserName, fileName: resp.fileName! }),
      { headers, skipLoader }
    );
  }

  findDocumentSubTypes(): Observable<DocumentSubType[]> {
    return this.get<DocumentSubType[]>('api/json/constants/docsubtypes/').pipe(map((resp) => resp.data!));
  }

  getVersion(docId: number, ipType: IP.TrifTypeEnum): Observable<FileVersionDO[]> {
    const skipCache = false;
    const options = {
      skipLoader: true,
      skipCache,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    };
    switch (ipType) {
      case IP.TypeEnum.Trademark:
        return this.getAndTransform<FileVersionDO[], FileVersionDO[]>(
          `api/json/trademarkfiles/${docId}/versions/`,
          (resp) => resp,
          options
        );
      case IP.TypeEnum.Matter:
        return this.getAndTransform<FileVersionDO[], FileVersionDO[]>(`api/json/matterfiles/${docId}/versions/`, (resp) => resp, options);
      case IP.TypeEnum.Patent:
      default:
        return this.getAndTransform<FileVersionDO[], FileVersionDO[]>(`api/json/casefiles/${docId}/versions/`, (resp) => resp, options);
    }
  }
}

export function formatFileDateTime(date: Date): string {
  const pad = (num: number) => (num < 10 ? `0${num}` : num.toString());

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are 0-based
  const day = pad(date.getDate());
  let hours = pad(date.getHours());
  let minutes = pad(date.getMinutes());
  let seconds = pad(date.getSeconds());
  if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
    const now = new Date();
    hours = pad(now.getHours());
    minutes = pad(now.getMinutes());
    seconds = pad(now.getSeconds());
  }

  // Get the timezone offset in minutes and convert it to hh:mm format
  const timezoneOffset = -date.getTimezoneOffset();
  const sign = timezoneOffset >= 0 ? '+' : '-';
  const offsetHours = pad(Math.floor(Math.abs(timezoneOffset) / 60));
  const offsetMinutes = pad(Math.abs(timezoneOffset) % 60);
  const timezone = `${sign}${offsetHours}${offsetMinutes}`;

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${timezone}`;
}
